<template>
  <div class="guide" v-if="guide">
    <div class="lp-container guide-wrap">
      <router-link class="g-back" :to="langPath('/guides')">
        <l-icon size="16">mdi-arrow-left</l-icon>{{ ui.back }}
      </router-link>

      <div class="g-meta">
        <span class="g-meta__tag">{{ category.name }}</span>
        <span class="g-meta__dot">·</span>
        <span>{{ ui.level[guide.level] }}</span>
        <span class="g-meta__dot">·</span>
        <span>{{ guide.minutes }} {{ ui.minutes }}</span>
        <span v-if="guide.plan !== 'free'" class="g-meta__plan">{{ ui.plan[guide.plan] }}</span>
      </div>

      <h1 class="lp-h1 g-title lp-balance">{{ guide.title }}</h1>
      <p class="lp-lead g-lead lp-balance">{{ guide.lead }}</p>

      <!-- Respuesta corta: lo mismo que el asistente lee en la app -->
      <aside class="g-summary">
        <span class="g-summary__label">{{ ui.summary }}</span>
        <p>{{ guide.answer }}</p>
      </aside>

      <!-- Plano completo, solo en la guía que explica la pantalla -->
      <guide-figure
        v-if="guide.overview"
        overview
        :labels="zoneLabels"
        :caption="ui.mapCaption"
        :alt="guide.title"
        class="g-map"
      />

      <h2 class="g-h2">{{ ui.steps }}</h2>
      <ol class="g-steps">
        <li v-for="(s, i) in guide.steps" :key="i" class="g-step">
          <div class="g-step__body">
            <span class="g-step__n">{{ i + 1 }}</span>
            <div>
              <h3 class="g-step__t">{{ s.t }}</h3>
              <p class="g-step__d">{{ s.d }}</p>
              <p v-if="s.tip" class="g-step__tip">
                <l-icon size="15" color="primary">lightbulb</l-icon>{{ s.tip }}
              </p>
            </div>
          </div>
          <guide-figure
            v-if="s.screen || s.zone"
            :screen="s.screen"
            :highlight="s.highlight"
            :zone="s.zone"
            :lang="lang"
            :step="i + 1"
            :caption="ui.stepCaption"
            :alt="s.t"
            class="g-step__fig"
          />
        </li>
      </ol>

      <template v-if="guide.examples && guide.examples.length">
        <h2 class="g-h2">{{ ui.examples }}</h2>
        <div class="g-ex">
          <div v-for="(block, i) in guide.examples" :key="i" class="g-ex__block">
            <h3 class="g-ex__title">{{ block.group }}</h3>
            <ul class="g-ex__list">
              <li v-for="(item, j) in block.items" :key="j">{{ item }}</li>
            </ul>
          </div>
        </div>
      </template>

      <template v-if="guide.tips && guide.tips.length">
        <h2 class="g-h2">{{ ui.tips }}</h2>
        <ul class="g-tips">
          <li v-for="(t, i) in guide.tips" :key="i">
            <l-icon size="17" color="primary">lightbulb</l-icon><span>{{ t }}</span>
          </li>
        </ul>
      </template>

      <template v-if="guide.faq && guide.faq.length">
        <h2 class="g-h2">{{ ui.faq }}</h2>
        <div class="g-faq">
          <div v-for="(f, i) in guide.faq" :key="i" class="g-faq__item">
            <h3 class="g-faq__q">{{ f.q }}</h3>
            <p class="g-faq__a">{{ f.a }}</p>
          </div>
        </div>
      </template>

      <div class="g-cta">
        <h3 class="lp-h3">{{ ui.ctaTitle }}</h3>
        <p class="g-cta__lead">{{ ui.ctaLead }}</p>
        <router-link class="lp-btn lp-btn--primary lp-btn--lg" :to="langPath('/download')">
          <l-icon small color="white">mdi-download</l-icon>{{ ui.ctaButton }}
        </router-link>
      </div>

      <div v-if="siblings.length" class="g-rel">
        <h3 class="g-rel__title">{{ ui.related }}</h3>
        <div class="g-rel__grid">
          <router-link
            v-for="r in siblings"
            :key="r.slug"
            :to="langPath(`/guides/${r.slug}`)"
            class="g-rel__card"
          >
            <span class="g-rel__icon"><l-icon size="18" color="primary">{{ r.icon }}</l-icon></span>
            <span class="g-rel__name">{{ r.title }}</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="lp-container g-404">
    <h1 class="lp-h2">{{ ui.notFound }}</h1>
    <router-link class="lp-btn lp-btn--ghost" :to="langPath('/guides')">{{ ui.back }}</router-link>
  </div>
</template>

<script>
import GuideFigure from './GuideFigure.vue';
import {
  CATEGORIES,
  GUIDE_UI,
  ZONE_LABELS,
  getGuide,
  guideLang,
  localizedCategory,
  localizedGuide,
  siblingGuides,
} from './guides';

export default {
  name: 'GuidePage',
  components: { GuideFigure },
  metaInfo() {
    if (!this.guide) return { title: 'Not found | Nukli' };
    return {
      title: this.guide.title,
      titleTemplate: '%s | Nukli',
      meta: [
        { name: 'description', content: this.guide.answer },
        { property: 'og:title', content: `${this.guide.title} | Nukli` },
        { property: 'og:description', content: this.guide.answer },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: `https://nukliapp.com/guides/${this.guide.slug}` },
      ],
      script: [{
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: this.guide.title,
          description: this.guide.answer,
          totalTime: `PT${this.guide.minutes}M`,
          step: (this.guide.steps || []).map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: s.t,
            text: s.d,
          })),
        },
      }],
    };
  },
  computed: {
    lang() {
      return guideLang(this.$i18n.locale);
    },
    ui() {
      return GUIDE_UI[this.lang];
    },
    zoneLabels() {
      return ZONE_LABELS[this.lang];
    },
    guide() {
      return localizedGuide(getGuide(this.$route.params.slug), this.lang);
    },
    category() {
      const cat = CATEGORIES.find((c) => c.key === (this.guide && this.guide.category));
      return cat ? localizedCategory(cat, this.lang) : { name: '' };
    },
    siblings() {
      return siblingGuides(this.$route.params.slug, this.lang);
    },
  },
  watch: {
    '$route.params.slug'() {
      window.scrollTo({ top: 0 });
    },
  },
  methods: {
    langPath(path) {
      const l = this.$route.params.lang;
      return l ? `/${l}${path}` : path;
    },
  },
};
</script>

<style scoped>
.guide { background: var(--lp-bg); min-height: calc(100vh - 64px); }
.guide-wrap { max-width: 820px; padding-top: 112px; padding-bottom: 96px; }

.g-back {
  display: inline-flex; align-items: center; gap: 7px;
  text-decoration: none; margin-bottom: 36px;
  font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 0.94rem;
  color: var(--lp-muted); transition: color 0.15s ease;
}
.g-back:hover { color: var(--lp-ink); }

.g-meta {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  font-size: 0.9rem; color: var(--lp-muted); margin-bottom: 18px;
}
.g-meta__tag { color: var(--lp-accent); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.g-meta__dot { opacity: 0.5; }
.g-meta__plan {
  margin-left: 4px; padding: 2px 10px; border-radius: 999px;
  background: var(--lp-accent-weak); color: var(--lp-accent);
  font-size: 0.8rem; font-weight: 600;
}

/* Cuerpo grande a propósito: es la pantalla que alguien lee con el móvil en
   una mano y la app en la otra, a veces con la vista cansada. */
.g-title { font-size: clamp(2.1rem, 4.2vw, 2.9rem); margin-bottom: 18px; }
.g-lead { margin-bottom: 30px; font-size: 1.22rem; }

.g-summary {
  border: 1px solid var(--lp-accent);
  background: var(--lp-accent-weak);
  border-radius: var(--lp-radius);
  padding: 22px 26px;
  margin-bottom: 44px;
}
.g-summary__label {
  display: block; margin-bottom: 8px;
  font-family: 'Space Grotesk', sans-serif; font-weight: 700;
  font-size: 0.82rem; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--lp-accent);
}
.g-summary p { margin: 0; font-size: 1.1rem; line-height: 1.7; color: var(--lp-ink); }

.g-map { margin-bottom: 48px; }

.g-h2 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.6rem; font-weight: 600; letter-spacing: -0.02em;
  color: var(--lp-ink); margin: 52px 0 22px;
}

.g-steps { list-style: none; margin: 0; padding: 0; }
.g-step {
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  background: var(--lp-surface);
  padding: 26px;
  margin-bottom: 18px;
}
.g-step__body { display: flex; gap: 18px; align-items: flex-start; }
.g-step__n {
  flex: none;
  width: 40px; height: 40px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-accent); color: #fff;
  font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 1.12rem;
}
.g-step__t {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.22rem; font-weight: 600; color: var(--lp-ink);
  margin: 6px 0 8px; line-height: 1.3;
}
.g-step__d { margin: 0; font-size: 1.09rem; line-height: 1.72; color: var(--lp-ink-2); }
.g-step__tip {
  display: flex; gap: 8px; align-items: flex-start;
  margin: 12px 0 0; padding: 12px 14px;
  background: var(--lp-bg-subtle); border-radius: 12px;
  font-size: 1rem; line-height: 1.55; color: var(--lp-ink-2);
}
.g-step__fig { margin-top: 22px; }

.g-ex { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.g-ex__block {
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius);
  background: var(--lp-surface);
  padding: 22px 24px;
}
.g-ex__title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.02rem; font-weight: 600; color: var(--lp-ink);
  margin: 0 0 12px;
}
.g-ex__list { margin: 0; padding: 0; list-style: none; }
.g-ex__list li {
  position: relative;
  padding: 9px 12px 9px 30px;
  margin-bottom: 6px;
  border-radius: 10px;
  background: var(--lp-bg-subtle);
  font-size: 1.03rem; line-height: 1.5; color: var(--lp-ink-2);
}
.g-ex__list li::before {
  content: '“';
  position: absolute; left: 11px; top: 8px;
  color: var(--lp-accent); font-size: 1.3rem; font-weight: 700; line-height: 1;
}

.g-tips { list-style: none; margin: 0; padding: 0; }
.g-tips li {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 16px 18px; margin-bottom: 10px;
  border: 1px solid var(--lp-line); border-radius: var(--lp-radius);
  background: var(--lp-bg-subtle);
  font-size: 1.06rem; line-height: 1.65; color: var(--lp-ink-2);
}

.g-faq__item { padding: 20px 0; border-top: 1px solid var(--lp-line); }
.g-faq__item:last-child { border-bottom: 1px solid var(--lp-line); }
.g-faq__q {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.12rem; font-weight: 600; color: var(--lp-ink); margin: 0 0 8px;
}
.g-faq__a { margin: 0; font-size: 1.06rem; line-height: 1.7; color: var(--lp-ink-2); }

.g-cta {
  margin-top: 64px; padding: 40px; text-align: center;
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius-lg);
  display: flex; flex-direction: column; align-items: center; gap: 10px;
}
.g-cta__lead { color: var(--lp-muted); margin: 0 0 14px; font-size: 1.05rem; }

.g-rel { margin-top: 64px; }
.g-rel__title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.1rem; font-weight: 600; color: var(--lp-ink); margin: 0 0 18px;
}
.g-rel__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.g-rel__card {
  display: flex; flex-direction: column; gap: 12px;
  padding: 20px; text-decoration: none;
  border: 1px solid var(--lp-line); border-radius: var(--lp-radius);
  background: var(--lp-surface);
  transition: border-color 0.2s ease, transform 0.2s ease;
}
.g-rel__card:hover { border-color: var(--lp-line-strong); transform: translateY(-3px); }
.g-rel__icon {
  width: 34px; height: 34px; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-accent-weak);
}
.g-rel__name {
  font-family: 'Space Grotesk', sans-serif; font-weight: 600;
  font-size: 0.98rem; line-height: 1.35; color: var(--lp-ink);
}

.g-404 {
  text-align: center; padding: 140px 24px;
  display: flex; flex-direction: column; align-items: center; gap: 20px;
}

@media (max-width: 719px) {
  .guide-wrap { padding-top: 96px; }
  .g-ex, .g-rel__grid { grid-template-columns: 1fr; }
  .g-step { padding: 20px; }
  .g-step__body { gap: 14px; }
  .g-cta { padding: 28px 20px; }
}
</style>
